import { SkyCheckbox } from "@skypointcloud/skypoint-ui";

/**
 * One checkbox holding one boolean (GUIDELINES.md §3.1).
 *
 * `SkyCheckbox` covers both shapes behind one signature: a group when `options`
 * is given, a single control when it is not. The cost is that `value` is
 * `string[] | boolean` and `onChange` receives `CheckboxState[] | boolean`, so
 * every caller that wants a plain boolean has to narrow the union itself — and
 * a caller that narrows it wrongly gets a checkbox that silently never ticks.
 * This component is that narrowing, written once.
 *
 * It is a wrapper, not a replacement: the rendering, the tokens, the focus ring
 * and the accessible name all still come from skypoint-ui. There is no second
 * design system here, which is why `no-restricted-imports` exempts this one path
 * from the `@/components/ui/*` ban.
 *
 * Shared across packages — connections, schedules, runs and admin all import it
 * from `@/components/ui/SingleCheckbox`.
 */
export interface SingleCheckboxProps {
  /** The visible label. Also the accessible name, so it is never optional. */
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  /** Help text under the control. */
  description?: string;
  disabled?: boolean;
  required?: boolean;
  /** `error` sets `aria-invalid`; the message is shown under the control. */
  validationState?: "none" | "error" | "warning" | "success";
  validationMessage?: string;
  size?: "medium" | "large";
  className?: string;
  testId?: string;
}

export function SingleCheckbox({
  label,
  checked,
  onChange,
  description,
  disabled,
  required,
  validationState,
  validationMessage,
  size,
  className,
  testId,
}: SingleCheckboxProps) {
  return (
    <SkyCheckbox
      label={label}
      description={description}
      value={checked}
      disabled={disabled}
      required={required}
      validationState={validationState}
      validationMessage={validationMessage}
      size={size}
      className={className}
      testId={testId}
      onChange={(next) => {
        // Single mode always answers with a boolean; the array arm belongs to
        // the group mode this component never enters. Narrowing rather than
        // casting means a future library change surfaces here as a no-op
        // instead of as `[object Object]` in a form value.
        if (typeof next === "boolean") onChange(next);
      }}
    />
  );
}
